/**
 * E-signature Service
 * Handles electronic signature validation and modal interactions
 * Replaces Angular Modal.password functionality from the original implementation
 */

export interface ESignatureValidation {
  username: string
  password: string
  timestamp?: string
  userAgent?: string
}

export interface ESignatureModalOptions {
  title?: string
  message: string
  requireUsername?: boolean
  requirePassword?: boolean
}

export interface ESignatureResult {
  success: boolean
  validation?: ESignatureValidation
  cancelled?: boolean
  error?: string
}

/**
 * E-signature validation service
 * This would integrate with your modal system (e.g., React Modal, MUI Dialog, etc.)
 */
export class ESignatureService {
  /**
   * Show e-signature modal and collect credentials
   * TODO: Implement actual iMedidata SAML authentication
   */
  async requestESignature(options: ESignatureModalOptions): Promise<ESignatureResult> {
    console.log('ESignature modal placeholder for:', options.title || options.message)

    // Placeholder implementation - returns mock credentials
    // In test mode, use environment variables if available
    const username = import.meta.env.VITE_ESIGN_USERNAME || 'mock-user'
    const password = import.meta.env.VITE_ESIGN_PASSWORD || 'mock-pass'

    return Promise.resolve({
      success: true,
      validation: {
        username,
        password,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      }
    })
  }

  /**
   * Validate e-signature requirements
   */
  validateSignature(validation: ESignatureValidation): boolean {
    if (!validation.username || !validation.password) {
      return false
    }

    // Add additional validation logic here
    return true
  }

  /**
   * Create e-signature data for API submission
   */
  createSignatureData(validation: ESignatureValidation): string {
    const signatureData = {
      ...validation,
      timestamp: validation.timestamp ?? new Date().toISOString(),
      userAgent: validation.userAgent ?? navigator.userAgent
    }

    return JSON.stringify(signatureData)
  }
}

// Export singleton instance
export const eSignatureService = new ESignatureService()

/**
 * Hook-like function for e-signature integration
 * Can be used in React components or converted to a custom hook
 */
export async function useESignature(
  requireESignature: boolean,
  options: ESignatureModalOptions
): Promise<ESignatureValidation | null> {
  if (!requireESignature) {
    return null
  }

  const result = await eSignatureService.requestESignature(options)

  if (!result.success || !result.validation) {
    throw new Error(result.error ?? 'E-signature cancelled or failed')
  }

  return result.validation
}

/**
 * Translation keys for e-signature text generation
 * These keys should be used with react-i18next's t() function
 */
export interface ESignatureTranslationKeys {
  DOCUMENT_MIGRATION_ESIGN_HEADER: string
  DOCUMENT_MIGRATION: string
  MIGRATIONS_WITOUT_CR: string
  CERT_COPY_MIGRATION_ESIGN_HEADER: string
  CERTIFIED_COPY: string
}

/**
 * Translated text values for e-signature generation
 * Should be obtained by calling t() function with the keys above
 */
export interface ESignatureTexts {
  DOCUMENT_MIGRATION_ESIGN_HEADER: string
  DOCUMENT_MIGRATION: string
  MIGRATIONS_WITOUT_CR: string
  CERT_COPY_MIGRATION_ESIGN_HEADER: string
  CERTIFIED_COPY: string
}

export function generateESignatureText(
  params: {
    createCR?: boolean
    isCertifiedCopy?: boolean
    requireESignature: boolean
  },
  texts: ESignatureTexts
): string {
  let textModal = ''

  if (params.requireESignature) {
    textModal += texts.DOCUMENT_MIGRATION_ESIGN_HEADER

    if (params.createCR) {
      textModal += texts.DOCUMENT_MIGRATION
    } else {
      textModal += texts.MIGRATIONS_WITOUT_CR
    }
  }

  if (params.isCertifiedCopy) {
    const separator = params.requireESignature ? '<br><br>' : ''
    textModal += separator + texts.CERT_COPY_MIGRATION_ESIGN_HEADER + texts.CERTIFIED_COPY
  }

  return textModal
}

/**
 * Gets the translation keys needed for e-signature text generation
 * Components should use these keys with react-i18next's t() function
 */
export function getESignatureTranslationKeys(params: {
  createCR?: boolean
  isCertifiedCopy?: boolean
  requireESignature: boolean
}): string[] {
  const keys: string[] = []

  if (params.requireESignature) {
    keys.push('DOCUMENT_MIGRATION_ESIGN_HEADER')

    if (params.createCR) {
      keys.push('DOCUMENT_MIGRATION')
    } else {
      keys.push('MIGRATIONS_WITOUT_CR')
    }
  }

  if (params.isCertifiedCopy) {
    keys.push('CERT_COPY_MIGRATION_ESIGN_HEADER', 'CERTIFIED_COPY')
  }

  return keys
}
